import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, closestCorners, useDraggable, useDroppable, useSensor, useSensors,
} from '@dnd-kit/core';
import { CalendarPlus, ChevronRight, Clock3, GripVertical, NotebookPen, Phone, UserRound } from 'lucide-react';
import { NieuweClientModal } from './ClientsPage.jsx';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { amsterdamDateStr, todayStr } from '../../utils/dateUtils.js';

const STAGES = ['nieuwe_lead', 'in_behandeling', 'kennismaking', 'proeftraining', 'afgerond'];

function stageLabel(stage) {
  return t(`prospects.stages.${stage}`);
}

function dayDiffFromToday(iso) {
  if (!iso) return null;
  const today = new Date(`${todayStr()}T12:00:00Z`);
  const target = new Date(`${amsterdamDateStr(iso)}T12:00:00Z`);
  return Math.round((target - today) / 86400000);
}

function getPriority(prospect) {
  const actionDiff = dayDiffFromToday(prospect.next_action_at);
  if (actionDiff === 0) return { rank: 1, key: 'today' };
  if (actionDiff !== null && actionDiff < 0) return { rank: 1, key: 'overdue', days: Math.abs(actionDiff) };
  if (actionDiff === 1) return { rank: 3, key: 'tomorrow' };
  if (actionDiff !== null) return { rank: 4, key: 'future', days: actionDiff };
  const idleSince = prospect.last_activity_at || prospect.created_at;
  const idleDays = idleSince ? Math.max(0, -dayDiffFromToday(idleSince)) : 0;
  return { rank: 2, key: 'idle', days: idleDays };
}

function priorityLabel(priority) {
  if (priority.key === 'today') return t('prospects.priority.today');
  if (priority.key === 'tomorrow') return t('prospects.priority.tomorrow');
  if (priority.key === 'overdue') return t('prospects.priority.overdue', { count: priority.days });
  if (priority.key === 'idle') return t('prospects.priority.idle', { count: priority.days });
  return t('prospects.priority.future', { count: priority.days });
}

function formatDateTime(value) {
  if (!value) return t('prospects.noAction');
  return new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Amsterdam', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(new Date(value));
}

function formatActionTime(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Amsterdam', hour: '2-digit', minute: '2-digit',
  }).format(new Date(value));
}

function completionLabel(reason) {
  return reason ? t(`prospects.completionReasons.${reason}`) : t('prospects.completionReasons.unknown');
}

function ProspectCard({ prospect, selected, onSelect, overlay = false }) {
  const priority = getPriority(prospect);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: prospect.id,
    data: { prospect },
    disabled: overlay,
  });
  const style = transform && !overlay ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`prospect-card priority-${priority.rank}${selected ? ' is-selected' : ''}${isDragging ? ' is-dragging' : ''}${overlay ? ' is-overlay' : ''}`}
      onClick={() => onSelect?.(prospect.id)}
    >
      <div className="prospect-card-topline">
        <button
          type="button"
          className="prospect-drag-handle"
          aria-label={t('prospects.dragLabel', { name: prospect.full_name })}
          {...listeners}
          {...attributes}
          onClick={event => event.stopPropagation()}
        >
          <GripVertical size={16} />
        </button>
        <span className={`prospect-priority-tag priority-${priority.rank}`}>{priorityLabel(priority)}</span>
      </div>
      <button type="button" className="prospect-card-main" onClick={() => onSelect?.(prospect.id)}>
        <span className="prospect-name">{prospect.full_name || prospect.email}</span>
        <span className="prospect-contact">{prospect.phone || prospect.email}</span>
        <span className="prospect-next-action">
          <Clock3 size={14} />
          <span>
            {priority.key === 'today' && <strong className="prospect-action-time">{formatActionTime(prospect.next_action_at)}</strong>}
            {prospect.next_action_note || t('prospects.noAction')}
          </span>
        </span>
        <span className="prospect-card-footer">
          <span>{prospect.coach_name || t('prospects.noCoach')}</span>
          <ChevronRight size={15} />
        </span>
      </button>
    </article>
  );
}

function ProspectColumn({ stage, prospects, selectedId, onSelect }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  return (
    <section className={`prospect-column stage-${stage}${isOver ? ' is-over' : ''}`} aria-label={stageLabel(stage)}>
      <header className="prospect-column-header">
        <div><span className="prospect-stage-dot" />{stageLabel(stage)}</div>
        <span className="prospect-stage-count">{prospects.length}</span>
      </header>
      <div ref={setNodeRef} className="prospect-column-body">
        {prospects.map(prospect => (
          <ProspectCard key={prospect.id} prospect={prospect} selected={selectedId === prospect.id} onSelect={onSelect} />
        ))}
        {prospects.length === 0 && <div className="prospect-column-empty">{t('prospects.emptyStage')}</div>}
      </div>
    </section>
  );
}

export default function ProspectsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();
  const [prospects, setProspects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [activeDragId, setActiveDragId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [note, setNote] = useState('');
  const [actionAt, setActionAt] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [savingAction, setSavingAction] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [completionPrompt, setCompletionPrompt] = useState(null);
  const [completionReason, setCompletionReason] = useState('');
  const [savingCompletion, setSavingCompletion] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: profileRows, error: profileError } = await supabase
        .from('profiles')
        .select('id, full_name, email, phone, created_at, client_status, deleted_at')
        .eq('role', 'client')
        .eq('client_status', 'prospect')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (profileError) throw profileError;
      const ids = (profileRows || []).map(row => row.id);
      if (!ids.length) { setProspects([]); return; }
      const [pipelineRes, linksRes] = await Promise.all([
        supabase.from('prospect_pipeline').select('*').in('prospect_id', ids),
        supabase.from('coach_clients').select('client_id, coach_id, is_primary').in('client_id', ids).in('status', ['active', 'inactive']),
      ]);
      if (pipelineRes.error) throw pipelineRes.error;
      if (linksRes.error) throw linksRes.error;
      const coachIds = [...new Set((linksRes.data || []).map(row => row.coach_id))];
      const { data: coachRows, error: coachError } = coachIds.length
        ? await supabase.from('profiles').select('id, full_name').in('id', coachIds)
        : { data: [], error: null };
      if (coachError) throw coachError;
      const pipelineMap = Object.fromEntries((pipelineRes.data || []).map(row => [row.prospect_id, row]));
      const coachNameMap = Object.fromEntries((coachRows || []).map(row => [row.id, row.full_name]));
      const coachByClient = {};
      (linksRes.data || []).sort((a, b) => Number(b.is_primary) - Number(a.is_primary)).forEach(row => {
        if (!coachByClient[row.client_id]) coachByClient[row.client_id] = {
          id: row.coach_id,
          name: coachNameMap[row.coach_id],
        };
      });
      const archiveThreshold = Date.now() - 7 * 86400000;
      setProspects((profileRows || []).map(row => ({
        ...row,
        stage: pipelineMap[row.id]?.stage || 'nieuwe_lead',
        next_action_at: pipelineMap[row.id]?.next_action_at || null,
        next_action_note: pipelineMap[row.id]?.next_action_note || null,
        last_activity_at: pipelineMap[row.id]?.last_activity_at || row.created_at,
        completion_reason: pipelineMap[row.id]?.completion_reason || null,
        completed_at: pipelineMap[row.id]?.completed_at || null,
        coach_id: coachByClient[row.id]?.id || null,
        coach_name: coachByClient[row.id]?.name || null,
      })).filter(row => row.stage !== 'afgerond' || !row.completed_at || new Date(row.completed_at).getTime() > archiveThreshold));
    } catch (error) {
      showError(t('prospects.loadError') + error.message);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const requestedProspect = new URLSearchParams(location.search).get('prospect');
    if (requestedProspect && prospects.some(row => row.id === requestedProspect)) {
      setSelectedId(requestedProspect);
    }
  }, [location.search, prospects]);

  useEffect(() => {
    if (location.state?.appointmentLogWarning) showError(location.state.appointmentLogWarning);
    else if (location.state?.toast) showSuccess(location.state.toast);
    if (location.state) navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, showError, showSuccess]);

  const selected = prospects.find(row => row.id === selectedId) || null;
  const activeProspect = prospects.find(row => row.id === activeDragId) || null;

  const grouped = useMemo(() => Object.fromEntries(STAGES.map(stage => [stage, prospects
    .filter(row => row.stage === stage)
    .sort((a, b) => {
      const pa = getPriority(a); const pb = getPriority(b);
      if (pa.rank !== pb.rank) return pa.rank - pb.rank;
      const dateA = a.next_action_at ? new Date(a.next_action_at).getTime() : new Date(a.last_activity_at || a.created_at).getTime();
      const dateB = b.next_action_at ? new Date(b.next_action_at).getTime() : new Date(b.last_activity_at || b.created_at).getTime();
      return dateA - dateB || (a.full_name || '').localeCompare(b.full_name || '', 'nl');
    })])), [prospects]);

  const loadActivities = useCallback(async prospectId => {
    if (!prospectId) { setActivities([]); return; }
    setActivitiesLoading(true);
    const { data, error } = await supabase
      .from('prospect_activities')
      .select('id, activity_type, body, metadata, created_at, actor:profiles!prospect_activities_actor_id_fkey(full_name)')
      .eq('prospect_id', prospectId)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false });
    if (error) showError(t('prospects.activitiesError') + error.message);
    setActivities(data || []);
    setActivitiesLoading(false);
  }, [showError]);

  useEffect(() => {
    if (!selected) return;
    setActionAt(selected.next_action_at ? new Date(selected.next_action_at).toLocaleString('sv-SE').slice(0, 16) : '');
    setActionNote(selected.next_action_note || '');
    loadActivities(selected.id);
  }, [selectedId]);

  function changeStage(prospect, stage) {
    if (!prospect || prospect.stage === stage) return;
    if (stage === 'afgerond') {
      setCompletionReason('');
      setCompletionPrompt(prospect);
      return;
    }
    persistStage(prospect, stage, null);
  }

  async function persistStage(prospect, stage, reason) {
    const previous = prospect.stage;
    setProspects(rows => rows.map(row => row.id === prospect.id ? { ...row, stage } : row));
    const { data, error } = await supabase.rpc('update_prospect_pipeline', {
      p_prospect_id: prospect.id,
      p_stage: stage,
      p_update_stage: true,
      p_update_action: false,
      p_completion_reason: reason,
    });
    if (error) {
      setProspects(rows => rows.map(row => row.id === prospect.id ? { ...row, stage: previous } : row));
      showError(t('prospects.stageSaveError') + error.message);
      return false;
    }
    setProspects(rows => rows.map(row => row.id === prospect.id ? { ...row, ...data } : row));
    if (selectedId === prospect.id) loadActivities(prospect.id);
    showSuccess(t('prospects.stageSaved', { stage: stageLabel(stage) }));
    return true;
  }

  async function completeProspect(event) {
    event.preventDefault();
    if (!completionPrompt || !completionReason) return;
    setSavingCompletion(true);
    const saved = await persistStage(completionPrompt, 'afgerond', completionReason);
    setSavingCompletion(false);
    if (!saved) return;
    setCompletionPrompt(null);
    setCompletionReason('');
  }

  async function saveAction(event) {
    event.preventDefault();
    if (!selected) return;
    setSavingAction(true);
    const { data, error } = await supabase.rpc('update_prospect_pipeline', {
      p_prospect_id: selected.id,
      p_next_action_at: actionAt ? new Date(actionAt).toISOString() : null,
      p_next_action_note: actionNote,
      p_update_stage: false,
      p_update_action: true,
      p_completion_reason: null,
    });
    setSavingAction(false);
    if (error) { showError(t('prospects.actionSaveError') + error.message); return; }
    setProspects(rows => rows.map(row => row.id === selected.id ? { ...row, ...data } : row));
    loadActivities(selected.id);
    showSuccess(t('prospects.actionSaved'));
  }

  async function addNote(event) {
    event.preventDefault();
    if (!selected || !note.trim()) return;
    setSavingNote(true);
    const { error } = await supabase.rpc('add_prospect_note', { p_prospect_id: selected.id, p_body: note.trim() });
    setSavingNote(false);
    if (error) { showError(t('prospects.noteSaveError') + error.message); return; }
    setNote('');
    setProspects(rows => rows.map(row => row.id === selected.id ? { ...row, last_activity_at: new Date().toISOString() } : row));
    loadActivities(selected.id);
    showSuccess(t('prospects.noteSaved'));
  }

  function planAppointment(kind) {
    if (!selected) return;
    navigate('/coach/les/nieuw', { state: {
      prefillPT: true,
      type: 'personal',
      clientId: selected.id,
      coachId: selected.coach_id,
      clientName: selected.full_name,
      prefillTrial: true,
      prospectAppointmentKind: kind,
      returnTo: '/coach/prospects',
    } });
  }

  return (
    <div className="prospects-page">
      {showCreateModal && (
        <NieuweClientModal
          initialClientType="prospect"
          onCreated={async () => {
            await load();
            showSuccess(t('prospects.prospectCreated'));
          }}
          onClose={() => setShowCreateModal(false)}
        />
      )}
      {completionPrompt && (
        <div className="modal-overlay">
          <form className="modal prospect-completion-modal" onSubmit={completeProspect}>
            <div className="modal-header">
              <h2 className="modal-title">{t('prospects.completeTitle')}</h2>
              <button type="button" className="modal-close" onClick={() => setCompletionPrompt(null)} aria-label={t('prospects.completeCancel')}>×</button>
            </div>
            <p>{t('prospects.completeIntro', { name: completionPrompt.full_name })}</p>
            <div className="prospect-completion-options">
              {['customer', 'no_interest', 'no_response'].map(reason => (
                <label key={reason} className={completionReason === reason ? 'is-selected' : ''}>
                  <input type="radio" name="completionReason" value={reason} checked={completionReason === reason} onChange={event => setCompletionReason(event.target.value)} />
                  <span>{completionLabel(reason)}</span>
                </label>
              ))}
            </div>
            <p className="prospect-completion-hint">{t('prospects.completeArchiveHint')}</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setCompletionPrompt(null)}>{t('prospects.completeCancel')}</button>
              <button type="submit" className="btn btn-primary" disabled={!completionReason || savingCompletion}>{savingCompletion ? t('common.laden') : t('prospects.completeConfirm')}</button>
            </div>
          </form>
        </div>
      )}
      <header className="prospects-header">
        <div>
          <div className="prospects-eyebrow">{t('prospects.eyebrow')}</div>
          <h1>{t('prospects.title')}</h1>
          <p>{t('prospects.subtitle')}</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(true)}><UserRound size={16} />{t('prospects.addProspect')}</button>
      </header>

      {loading ? <div className="loading-center"><span className="spinner" /></div> : (
        <div className={`prospects-workspace${selected ? ' has-detail' : ''}`}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={({ active }) => setActiveDragId(active.id)}
            onDragCancel={() => setActiveDragId(null)}
            onDragEnd={({ active, over }) => {
              setActiveDragId(null);
              if (over && STAGES.includes(over.id)) changeStage(active.data.current?.prospect, over.id);
            }}
          >
            <div className="prospect-board">
              {STAGES.map(stage => <ProspectColumn key={stage} stage={stage} prospects={grouped[stage]} selectedId={selectedId} onSelect={setSelectedId} />)}
            </div>
            <DragOverlay>{activeProspect ? <ProspectCard prospect={activeProspect} overlay /> : null}</DragOverlay>
          </DndContext>

          {selected && (
            <aside className="prospect-detail" aria-label={t('prospects.detailTitle', { name: selected.full_name })}>
              <div className="prospect-detail-head">
                <button type="button" className="prospect-detail-close" onClick={() => setSelectedId(null)} aria-label={t('prospects.closeDetail')}>×</button>
                <span className={`prospect-detail-stage stage-${selected.stage}`}>{stageLabel(selected.stage)}</span>
                {selected.stage === 'afgerond' && <span className="prospect-completion-reason">{completionLabel(selected.completion_reason)}</span>}
                <h2>{selected.full_name}</h2>
                <div className="prospect-detail-contact">
                  {selected.phone && <a href={`tel:${selected.phone}`}><Phone size={14} />{selected.phone}</a>}
                  <a href={`mailto:${selected.email}`}>{selected.email}</a>
                </div>
              </div>

              <div className="prospect-detail-actions">
                <button type="button" className="btn btn-primary" onClick={() => planAppointment('kennismaking')}><CalendarPlus size={16} />{t('prospects.planIntro')}</button>
                <button type="button" className="btn btn-secondary" onClick={() => planAppointment('proeftraining')}><CalendarPlus size={16} />{t('prospects.planTrial')}</button>
              </div>

              <form className="prospect-action-form" onSubmit={saveAction}>
                <h3>{t('prospects.nextActionTitle')}</h3>
                <label className="form-group"><span className="form-label">{t('prospects.nextAction')}</span><input className="form-input" value={actionNote} onChange={e => setActionNote(e.target.value)} maxLength={500} /></label>
                <label className="form-group"><span className="form-label">{t('prospects.actionMoment')}</span><input type="datetime-local" className="form-input" value={actionAt} onChange={e => setActionAt(e.target.value)} /></label>
                <button className="btn btn-ghost" disabled={savingAction}>{savingAction ? t('common.laden') : t('prospects.saveAction')}</button>
              </form>

              <form className="prospect-note-form" onSubmit={addNote}>
                <h3><NotebookPen size={17} />{t('prospects.notesTitle')}</h3>
                <textarea className="form-input" rows="3" maxLength={4000} value={note} onChange={e => setNote(e.target.value)} placeholder={t('prospects.notePlaceholder')} />
                <button className="btn btn-primary" disabled={savingNote || !note.trim()}>{savingNote ? t('common.laden') : t('prospects.addNote')}</button>
              </form>

              <div className="prospect-timeline">
                {activitiesLoading ? <div className="loading-center"><span className="spinner" /></div> : activities.length === 0 ? (
                  <p className="prospect-empty-activity">{t('prospects.noActivities')}</p>
                ) : activities.map(activity => (
                  <article key={activity.id} className={`prospect-activity type-${activity.activity_type}`}>
                    <span className="prospect-activity-dot" />
                    <div>
                      <strong>{t(`prospects.activity.${activity.activity_type}`)}</strong>
                      {activity.activity_type === 'stage_change' && <p>{stageLabel(activity.metadata?.from)} → {stageLabel(activity.metadata?.to)}{activity.metadata?.completion_reason ? ` · ${completionLabel(activity.metadata.completion_reason)}` : ''}</p>}
                      {activity.body && <p>{activity.body}</p>}
                      <small>{activity.actor?.full_name || t('prospects.systemActor')} · {formatDateTime(activity.created_at)}</small>
                    </div>
                  </article>
                ))}
              </div>
              <Link className="prospect-full-link" to={`/coach/clients/${selected.id}`}>{t('prospects.openRelation')}<ChevronRight size={15} /></Link>
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
